import {
  type AuthenticationResult,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  PublicClientApplication,
} from '@azure/msal-browser';

const CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID as string;
const REDIRECT_URI = `${window.location.origin}/redirect.html`;
const SCOPES = ['openid', 'profile', 'email', 'User.Read'];
const POPUP_BLOCKED_ERROR_CODES: string[] = [
  BrowserAuthErrorCodes.popupWindowError,
  BrowserAuthErrorCodes.emptyWindowError,
];

interface MsalWebTokens {
  idToken: string;
  accessToken: string;
}

interface MsalWebRedirectResult extends MsalWebTokens {
  state: string;
}

const msalInstance: Promise<PublicClientApplication> = (async () => {
  const instance = new PublicClientApplication({
    auth: {
      clientId: CLIENT_ID,
      authority: 'https://login.microsoftonline.com/common',
      redirectUri: REDIRECT_URI,
      postLogoutRedirectUri: REDIRECT_URI,
    },
    cache: { cacheLocation: 'localStorage' },
  });

  await instance.initialize();

  return instance;
})();

const toTokens = (result: AuthenticationResult): MsalWebTokens => ({
  idToken: result.idToken,
  accessToken: result.accessToken,
});

export const isMsalPopupBlockedError = (error: unknown): boolean =>
  error instanceof BrowserAuthError && POPUP_BLOCKED_ERROR_CODES.includes(error.errorCode);

export async function msalWebLogin(nonce: string): Promise<MsalWebTokens> {
  const instance = await msalInstance;

  const result = await instance.loginPopup({
    scopes: SCOPES,
    nonce,
  });

  return toTokens(result);
}

export async function msalWebLoginRedirect(nonce: string, state: string): Promise<never> {
  const instance = await msalInstance;

  await instance.loginRedirect({
    scopes: SCOPES,
    nonce,
    state,
  });

  return new Promise<never>(() => undefined);
}

export async function msalWebHandleRedirect(): Promise<MsalWebRedirectResult | null> {
  const instance = await msalInstance;
  const result = await instance.handleRedirectPromise();

  if (!result?.state) {
    return null;
  }

  return { ...toTokens(result), state: result.state };
}
